import React, { useState, useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Fab,
  Zoom,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Event as EventIcon,
  Church as ChurchIcon,
  Receipt as ReceiptIcon,
  ArrowBack as ArrowBackIcon,
  PersonAdd as PersonAddIcon,
  Autorenew as AutorenewIcon,
  MenuBook as BTIcon,
  People as TeacherIcon,
  Quiz as BQIcon,
  EmojiEvents as OlympicIcon,
  Terrain as SummitIcon,
  Campaign as PopupIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Logout as LogoutIcon,
  WorkspacePremium as CertIcon,
  Inventory2 as RentalIcon,
  ApartmentOutlined as HqIcon,
} from '@mui/icons-material';
import AdminLoginGate from './AdminLoginGate';
import AdminBell, { useCertNotifications } from '../features/btcert/admin/AdminBell';

const drawerWidth = 240;

interface MenuItem {
  text: string;
  path: string;
  icon: React.ReactNode;
  /**
   * 이 SPA 의 라우트가 아니라 별도 앱(별도 컨테이너)일 때 true.
   * RouterLink 로 열면 React Router 가 자기 라우트 표에서 찾다가 못 찾고
   * `<Route path="*">` 로 떨어져 `/` 로 튕긴다 — 전체 페이지 로드가 필요하다.
   */
  external?: boolean;
  /** 완성보고서 처리 대기 장수 배지 표시 */
  certBadge?: boolean;
  indent?: boolean;
}

const menuItems: MenuItem[] = [
  // 본부 포털(awana-hq-app, nginx /hq/ → 별도 컨테이너) — 메뉴 맨 위
  { text: '본부 포털 (HQ)', path: '/hq/', icon: <HqIcon />, external: true },
  { text: '대시보드', path: '/admin', icon: <DashboardIcon /> },
  { text: '이벤트 관리', path: '/admin/events', icon: <EventIcon /> },
  { text: '팝업 관리', path: '/admin/popups', icon: <PopupIcon /> },
  { text: '교회 관리', path: '/admin/churches', icon: <ChurchIcon /> },
  { text: '신규등록', path: '/admin/new-registration', icon: <PersonAddIcon /> },
  { text: '재등록', path: '/admin/renew-registration', icon: <AutorenewIcon /> },
  { text: 'Awana 리더십 교육', path: '/admin/bt', icon: <BTIcon /> },
  { text: '교사 관리', path: '/admin/bt-teachers', icon: <TeacherIcon />, certBadge: true },
  { text: '완성보고서 승인', path: '/admin/bt-teachers?tab=reports', icon: <CertIcon />, certBadge: true, indent: true },
  { text: 'BQ 관리', path: '/admin/bq', icon: <BQIcon /> },
  { text: '올림픽 관리', path: '/admin/olympic', icon: <OlympicIcon /> },
  { text: '영수증 관리', path: '/admin/receipts', icon: <ReceiptIcon /> },
  { text: '현장등록', path: '/admin/onsite', icon: <ReceiptIcon /> },
  { text: '접수 현황', path: '/admin/receipt-status', icon: <ReceiptIcon /> },
  { text: '물품 대여', path: '/admin/rental', icon: <RentalIcon /> },
  { text: 'Summit 관리', path: '/summitadmin/', icon: <SummitIcon />, external: true },
];

const AdminLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const [authOpen, setAuthOpen] = useState(true);
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'mini' | null>(null);
  const [error, setError] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (role) setAuthOpen(false);
  }, [role]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 관리자 암호는 서버(bt-service)에서 확인하고 httpOnly 쿠키를 받는다.
  // (이전에는 화면 코드 안에서만 비교해서 API 가 보호되지 않았음)
  useEffect(() => {
    fetch('/api/bt/admin-auth/me', { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (data?.data?.role) setRole(data.data.role); })
      .catch(() => undefined);
  }, []);

  const handleAuth = async () => {
    try {
      const response = await fetch('/api/bt/admin-auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.data?.role) {
        setRole(data.data.role);
        setError('');
        return;
      }
      // TODO(배포 후 삭제): 새 bt-service 가 배포되기 전(404)에만 예전 방식으로 확인
      if (response.status === 404) return legacyAuth();
      setError(data?.message || '암호가 올바르지 않습니다.');
    } catch {
      legacyAuth();
    }
  };

  /** 예전 화면 내 암호 확인 — 새 bt-service 배포 전 과도기용. 배포가 끝나면 이 함수와 호출부를 지우세요. */
  const legacyAuth = () => {
    if (password === '1983') setRole('admin');
    else if (password === '0000') setRole('mini');
    else setError('암호가 올바르지 않습니다.');
  };

  const { data: certNotif, refresh: refreshCert } = useCertNotifications(role !== null);
  const certPending = certNotif?.pendingSheets || 0;

  const isMenuDisabled = (path: string) => false;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          관리자 메뉴
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              // 별도 앱은 <a> 로 전체 페이지 로드를 해야 한다. RouterLink 로 열면
              // React Router 가 자기 라우트 표에서 못 찾고 `/` 로 튕긴다.
              {...(item.external
                ? { component: 'a' as const, href: item.path }
                : { component: RouterLink, to: item.path })}
              // 외부 앱은 이 SPA 의 pathname 과 절대 같아질 수 없으므로 선택 표시 대상이 아니다.
              selected={!item.external && `${location.pathname}${new URLSearchParams(location.search).get('tab') === 'reports' ? '?tab=reports' : ''}` === item.path}
              disabled={isMenuDisabled(item.path)}
            >
              <ListItemIcon sx={item.indent ? { pl: 2 } : undefined}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} primaryTypographyProps={item.indent ? { fontSize: 14 } : undefined} />
              {item.certBadge && certPending > 0 && (
                <Badge color="error" badgeContent={certPending} max={99} sx={{ mr: 1.5 }} />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      {/* <List>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/">
            <ListItemIcon>
              <ArrowBackIcon />
            </ListItemIcon>
            <ListItemText primary="" />
          </ListItemButton>
        </ListItem>
      </List> */}
    </div>
  );

  // HQ 로그인은 메인 관리자 접근을, BT 인증은 수료증 API 접근과 admin/mini 권한을 확인한다.
  return (
    <AdminLoginGate>
      {(staff, logout) => (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            AWANA 관리자
          </Typography>
          <Typography variant="body2" noWrap sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
            {staff.name}{staff.team ? ` · ${staff.team}` : ''}
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>로그아웃</Button>
          {role && <AdminBell data={certNotif} refresh={refreshCert} />}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          // 휴대폰에서 넓은 표·탭 때문에 화면 전체가 옆으로 밀리지 않게(내용이 자기 칸 안에서 스크롤)
          minWidth: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet context={{ role, staff, certPending, refreshCert }} />
        <Zoom in={showScrollTop}>
          <Fab
            color="primary"
            size="small"
            aria-label="scroll back to top"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1000,
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        </Zoom>
        <Dialog open={authOpen} disableEscapeKeyDown>
          <DialogTitle>BT 관리자 인증</DialogTitle>
          <DialogContent>
            <TextField
              label="암호 입력"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') handleAuth(); }}
              error={!!error}
              helperText={error}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAuth} variant="contained">확인</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
      )}
    </AdminLoginGate>
  );
};

export default AdminLayout;
